/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { CustomFontFamilySelector } from './customFontFamilySelector';
import { LintEditorFormatPropsType, FontSizeData } from './lintEditorType';
import { EditorFontSizeSelector, EditorFontUnitSelector } from './styledContents';
import {
  applyStylesToList,
  formatFontSize,
  isBulletList,
  isItalicOrNormal,
  isOrderedList,
  isWholeContentSelectedInEditor,
} from '../editorExtensions/utilityFunctions';
import { handleSelection } from './utilityFunctions';

export function FontSectionContent(props: LintEditorFormatPropsType) {
  const {
    editor,
    editorConfig,
    fontFamilyName,
    fontSizeValue,
    setIsItalicButtonActive,
    isDrogAndDrop,
  } = props;
  const [fontFamilyState, setFontFamilyState] = useState(fontFamilyName);
  const [fontSizeValueState, setFontSizeValueState] = useState(fontSizeValue);

  useEffect(() => {
    setFontFamilyState(fontFamilyName);
    setFontSizeValueState(fontSizeValue);
    if (props.fontStyleValue === 'italic' && editor && !props.enableIcon) {
      editor.chain().focus().setItalic().run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontFamilyName, fontSizeValue]);
  if (!editor) {
    return null;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const setFontName = (fontNameWithsttyle: string): void => {
    const { from, to } = editor.state.selection;
    if (setIsItalicButtonActive) setIsItalicButtonActive(false);
    const fontName = fontNameWithsttyle.replace(/-(italic|normal)$/i, '').trim();
    const type = isItalicOrNormal(fontNameWithsttyle);
    const fontStyleType = type === '' ? 'normal' : type;

    // if (this.isLinkText()) {
    //   this.editor.chain().focus().setLinkFont(f).run();
    //   return;
    // }
    const wholeSelectionStatus = isWholeContentSelectedInEditor(editor, from, to);
    editor.chain().focus().setFontFamily(fontName).run();
    editor.chain().focus().setFontStyle(fontStyleType).run();
    if (fontStyleType === 'italic') {
      editor.chain().focus().setItalic().run();
    } else {
      editor.chain().focus().unsetItalic().run();
    }
    setFontFamilyState(fontName);
    const paragraphSelections = handleSelection(editor);
    const singleArray =
      paragraphSelections.length === 1 && paragraphSelections[0].status === 'PARTIAL';
    if (
      (isBulletList(editor) || isOrderedList(editor)) &&
      !singleArray &&
      paragraphSelections.length > 0
    ) {
      applyStylesToList(
        {
          fontFamily: fontName,
        },
        'fontFamily',
        editor,
        from,
        to,
      );
      applyStylesToList(
        {
          fontStyle: fontStyleType,
        },
        'fontStyle',
        editor,
        from,
        to,
      );
      editor.chain().focus().setTextSelection({ from, to }).run();
      return;
    }
    if (fontName === '') {
      editor.chain().focus().unsetFontFamily().run();
      return;
    }
    if (wholeSelectionStatus.status) {
      applyStylesToList(
        {
          fontFamily: fontName,
        },
        'fontFamily',
        editor,
        from,
        to,
      );
      applyStylesToList(
        {
          fontStyle: fontStyleType,
        },
        'fontStyle',
        editor,
        from,
        to,
      );
      editor.chain().focus().setTextSelection({ from, to }).run();
      return;
    }
  };

  const setFontWithMetrics = (size: FontSizeData, isTableCell?: boolean): void => {
    const { from, to } = editor.state.selection;
    if (size === null) {
      editor.chain().focus().unsetFontSize().run();
      return;
    }
    const wholeSelectionStatus = isWholeContentSelectedInEditor(editor, from, to);
    setFontSizeValueState(size);
    const paragraphSelections = handleSelection(editor);
    const singleArray =
      paragraphSelections.length === 1 && paragraphSelections[0].status === 'PARTIAL';
    const nodeType = editor.state.selection.$from.node().type.name;
    const nodeTypeList = ['listItem', 'paragraph', 'text', 'heading'];
    nodeTypeList.push(nodeType);
    if (wholeSelectionStatus.status) editor.chain().focus().setFontSize(size).run();
    if (
      (isTableCell || isBulletList(editor) || isOrderedList(editor)) &&
      !singleArray &&
      paragraphSelections.length > 0
    ) {
      editor
        .chain()
        .focus()
        .updateNodeStylesInRange(
          nodeTypeList,
          { fontSize: formatFontSize(size) },
          'fontSize',
          from,
          to,
        )
        .run();
      editor.chain().focus().setTextSelection({ from, to }).run();
      return;
    }
    if (wholeSelectionStatus.status) {
      editor
        .chain()
        .focus()
        .updateNodeStylesInRange(
          nodeTypeList,
          { fontSize: formatFontSize(size) },
          'fontSize',
          from,
          to,
        )
        .run();
      editor.chain().focus().setTextSelection({ from, to }).run();
    }
    editor.chain().focus().setFontSize(size).run();
  };
  const setFontSizeMetricData = (fontSizeDataContent: FontSizeData | null) => {
    if (!fontSizeDataContent) return;
    setFontWithMetrics(fontSizeDataContent);
  };

  return (
    <>
      <CustomFontFamilySelector
        customFontList={editorConfig?.fontFamilyList ?? []}
        fontName={fontFamilyState}
        onFontFamilyChange={(value) => {
          setFontName(value);
        }}
        editor={editor}
        setEnableIcon={props.setEnableIcon}
        fontStyleValue={props.fontStyleValue}
        isItalicButtonActive={props.isItalicButtonActive}
      />
      <EditorFontSizeSelector activeSize={fontSizeValueState} onChange={setFontSizeMetricData} />
      {!isDrogAndDrop && (
        <EditorFontUnitSelector activeSize={fontSizeValueState} onChange={setFontSizeMetricData} />
      )}
    </>
  );
}
